import React, { useState, useMemo, useEffect } from 'react';
import { Calculator, DollarSign, Percent, Calendar, Car, Info, MapPin, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { track } from '../lib/analytics';

// Canadian province tax rates
const provinces = [
  { code: 'MB', name: 'Manitoba', gst: 5, pst: 7, hst: 0, total: 12 },
  { code: 'AB', name: 'Alberta', gst: 5, pst: 0, hst: 0, total: 5 },
  { code: 'BC', name: 'British Columbia', gst: 5, pst: 7, hst: 0, total: 12 },
  { code: 'SK', name: 'Saskatchewan', gst: 5, pst: 6, hst: 0, total: 11 },
  { code: 'ON', name: 'Ontario', gst: 0, pst: 0, hst: 13, total: 13 },
  { code: 'QC', name: 'Quebec', gst: 5, pst: 9.975, hst: 0, total: 14.975 },
  { code: 'NB', name: 'New Brunswick', gst: 0, pst: 0, hst: 15, total: 15 },
  { code: 'NS', name: 'Nova Scotia', gst: 0, pst: 0, hst: 15, total: 15 },
  { code: 'PE', name: 'Prince Edward Island', gst: 0, pst: 0, hst: 15, total: 15 },
  { code: 'NL', name: 'Newfoundland & Labrador', gst: 0, pst: 0, hst: 15, total: 15 },
  { code: 'YT', name: 'Yukon', gst: 5, pst: 0, hst: 0, total: 5 },
  { code: 'NT', name: 'Northwest Territories', gst: 5, pst: 0, hst: 0, total: 5 },
  { code: 'NU', name: 'Nunavut', gst: 5, pst: 0, hst: 0, total: 5 },
];

// Canadian Luxury Tax threshold
const LUXURY_TAX_THRESHOLD = 100000;

const DEFAULTS = { price: 25000, down: 5000, rate: 6.9, term: 60, province: 'MB' };
let calculatorUseTracked = false; // calculator_use fires once per page load

const LoanCalculator = () => {
  const navigate = useNavigate();
  const [vehiclePrice, setVehiclePrice] = useState(DEFAULTS.price);
  const [downPayment, setDownPayment] = useState(DEFAULTS.down);
  const [interestRate, setInterestRate] = useState(DEFAULTS.rate);
  const [loanTerm, setLoanTerm] = useState(DEFAULTS.term);
  const [province, setProvince] = useState(DEFAULTS.province); // Default to Manitoba
  const [showDetails, setShowDetails] = useState(false);

  // Record calculator use once the visitor has changed something and stopped dragging.
  useEffect(() => {
    if (calculatorUseTracked) return undefined;
    const touched = vehiclePrice !== DEFAULTS.price || downPayment !== DEFAULTS.down
      || interestRate !== DEFAULTS.rate || loanTerm !== DEFAULTS.term || province !== DEFAULTS.province;
    if (!touched) return undefined;
    const t = setTimeout(() => {
      calculatorUseTracked = true;
      track('calculator_use', { price: vehiclePrice, term: loanTerm });
    }, 1500);
    return () => clearTimeout(t);
  }, [vehiclePrice, downPayment, interestRate, loanTerm, province]);

  const selectedProvince = provinces.find(p => p.code === province) || provinces[0];

  const calculations = useMemo(() => {
    // Calculate sales tax
    const salesTaxRate = selectedProvince.total / 100;
    const salesTax = vehiclePrice * salesTaxRate;
    
    // Calculate luxury tax (Canadian federal luxury tax on vehicles over $100,000)
    // Luxury tax = lesser of: 20% of amount over $100,000 OR 10% of full price
    let luxuryTax = 0;
    if (vehiclePrice > LUXURY_TAX_THRESHOLD) {
      const amountOver = vehiclePrice - LUXURY_TAX_THRESHOLD;
      const option1 = amountOver * 0.20; // 20% of amount over threshold
      const option2 = vehiclePrice * 0.10; // 10% of full price
      luxuryTax = Math.min(option1, option2);
    }
    
    // Total vehicle cost including taxes
    const totalVehicleCost = vehiclePrice + salesTax + luxuryTax;
    
    // Amount to finance (after down payment)
    const principal = totalVehicleCost - downPayment;
    const monthlyRate = interestRate / 100 / 12;
    const numPayments = loanTerm;
    
    // Monthly payment formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
    let monthlyPayment = 0;
    if (monthlyRate > 0 && principal > 0) {
      monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
    } else if (principal > 0) {
      monthlyPayment = principal / numPayments;
    }
    
    const totalPayment = monthlyPayment * numPayments;
    const totalInterest = totalPayment - principal;
    
    return {
      monthlyPayment: isFinite(monthlyPayment) && monthlyPayment > 0 ? monthlyPayment : 0,
      totalPayment: isFinite(totalPayment) && totalPayment > 0 ? totalPayment : 0,
      totalInterest: isFinite(totalInterest) && totalInterest > 0 ? totalInterest : 0,
      principal: principal > 0 ? principal : 0,
      salesTax,
      luxuryTax,
      totalVehicleCost,
      hasLuxuryTax: luxuryTax > 0
    };
  }, [vehiclePrice, downPayment, interestRate, loanTerm, selectedProvince]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const loanTermOptions = [
    { value: 24, label: '24 mo' },
    { value: 36, label: '36 mo' },
    { value: 48, label: '48 mo' },
    { value: 60, label: '60 mo' },
    { value: 72, label: '72 mo' },
    { value: 84, label: '84 mo' }
  ];

  return (
    <section className="py-20 lg:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="h-px w-8 bg-red-600"></span>
            <span className="text-red-600 font-semibold text-xs uppercase tracking-[0.22em]">Payment Estimator</span>
            <span className="h-px w-8 bg-red-600"></span>
          </div>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 tracking-tight mb-4">
            <Calculator className="inline-block mr-3 text-red-600 align-middle" size={36} />
            Loan Calculator
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Estimate your monthly payments including taxes for your province
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Calculator Inputs */}
          <div className="bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm p-6 sm:p-8">
            <h3 className="text-xl font-display font-semibold text-gray-900 tracking-tight mb-6">Enter Your Details</h3>
            
            {/* Province Selection */}
            <div className="mb-6">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <MapPin size={18} className="mr-2 text-red-600" />
                Province
              </label>
              <select
                value={province}
                onChange={(e) => setProvince(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                data-testid="calc-province"
              >
                {provinces.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.name} ({p.total}% tax)
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {selectedProvince.hst > 0 
                  ? `HST: ${selectedProvince.hst}%` 
                  : `GST: ${selectedProvince.gst}%${selectedProvince.pst > 0 ? ` + PST: ${selectedProvince.pst}%` : ''}`
                }
              </p>
            </div>

            {/* Vehicle Price */}
            <div className="mb-6">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Car size={18} className="mr-2 text-red-600" />
                Vehicle Price
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={vehiclePrice}
                  onChange={(e) => setVehiclePrice(Number(e.target.value))}
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  min="0"
                  step="500"
                  data-testid="calc-vehicle-price"
                />
              </div>
              <input
                type="range"
                value={vehiclePrice}
                onChange={(e) => setVehiclePrice(Number(e.target.value))}
                min="5000"
                max="150000"
                step="500"
                className="w-full mt-2 accent-red-600"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>$5,000</span>
                <span>$150,000</span>
              </div>
              
              {/* Luxury Tax Warning */}
              {vehiclePrice > LUXURY_TAX_THRESHOLD && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-red-700">
                    Federal Luxury Tax applies to vehicles over $100,000
                  </p>
                </div>
              )}
            </div>

            {/* Down Payment */}
            <div className="mb-6">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <DollarSign size={18} className="mr-2 text-red-600" />
                Down Payment
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={downPayment}
                  onChange={(e) => setDownPayment(Number(e.target.value))}
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  min="0"
                  step="500"
                  data-testid="calc-down-payment"
                />
              </div>
              <input
                type="range"
                value={downPayment}
                onChange={(e) => setDownPayment(Number(e.target.value))}
                min="0"
                max={calculations.totalVehicleCost * 0.5}
                step="500"
                className="w-full mt-2 accent-red-600"
              />
            </div>

            {/* Interest Rate */}
            <div className="mb-6">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Percent size={18} className="mr-2 text-red-600" />
                Interest Rate (APR)
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={interestRate}
                  onChange={(e) => setInterestRate(Number(e.target.value))}
                  className="w-full pl-4 pr-8 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  min="0"
                  max="30"
                  step="0.1"
                  data-testid="calc-interest-rate"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">%</span>
              </div>
              <input
                type="range"
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                min="0"
                max="20"
                step="0.1"
                className="w-full mt-2 accent-red-600"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0%</span>
                <span>20%</span>
              </div>
            </div>

            {/* Loan Term */}
            <div className="mb-2">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Calendar size={18} className="mr-2 text-red-600" />
                Loan Term
              </label>
              <div className="grid grid-cols-6 gap-2">
                {loanTermOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setLoanTerm(option.value)}
                    className={`py-2 px-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                      loanTerm === option.value
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid={`term-${option.value}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Results Display */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl ring-1 ring-gray-800 shadow-sm p-6 sm:p-8 text-white">
            <h3 className="text-xl font-display font-semibold tracking-tight mb-6">Your Estimated Payment</h3>
            
            {/* Monthly Payment - Main Display */}
            <div className="bg-white/10 rounded-xl p-6 mb-6 text-center">
              <p className="text-gray-400 text-sm mb-2">Monthly Payment</p>
              <p className="text-4xl sm:text-5xl font-bold text-white" data-testid="monthly-payment">
                {formatCurrency(calculations.monthlyPayment)}
              </p>
              <p className="text-gray-400 text-sm mt-2">per month for {loanTerm} months</p>
            </div>

            {/* Tax Summary */}
            <div className="bg-white/5 rounded-lg p-4 mb-4">
              <p className="text-gray-400 text-xs mb-2 uppercase tracking-wide">Tax Summary ({selectedProvince.name})</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-500">Sales Tax ({selectedProvince.total}%)</p>
                  <p className="text-lg font-semibold">{formatCurrency(calculations.salesTax)}</p>
                </div>
                {calculations.hasLuxuryTax && (
                  <div>
                    <p className="text-xs text-red-400">Luxury Tax</p>
                    <p className="text-lg font-semibold text-red-400">{formatCurrency(calculations.luxuryTax)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-gray-400 text-xs mb-1">Amount Financed</p>
                <p className="text-xl font-semibold">{formatCurrency(calculations.principal)}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-gray-400 text-xs mb-1">Total Interest</p>
                <p className="text-xl font-semibold text-red-400">{formatCurrency(calculations.totalInterest)}</p>
              </div>
            </div>

            {/* Total Cost */}
            <div className="bg-white/5 rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center">
                <p className="text-gray-400">Total Cost of Loan</p>
                <p className="text-2xl font-bold">{formatCurrency(calculations.totalPayment)}</p>
              </div>
            </div>

            {/* Details Toggle */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-4 transition-colors"
            >
              <Info size={16} />
              {showDetails ? 'Hide' : 'Show'} full breakdown
            </button>

            {showDetails && (
              <div className="bg-white/5 rounded-lg p-4 mb-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Vehicle Price</span>
                  <span>{formatCurrency(vehiclePrice)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Sales Tax ({selectedProvince.total}%)</span>
                  <span>+ {formatCurrency(calculations.salesTax)}</span>
                </div>
                {calculations.hasLuxuryTax && (
                  <div className="flex justify-between text-red-400">
                    <span>Federal Luxury Tax</span>
                    <span>+ {formatCurrency(calculations.luxuryTax)}</span>
                  </div>
                )}
                <div className="border-t border-white/10 my-2"></div>
                <div className="flex justify-between font-medium">
                  <span className="text-gray-300">Total Vehicle Cost</span>
                  <span>{formatCurrency(calculations.totalVehicleCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Down Payment</span>
                  <span>- {formatCurrency(downPayment)}</span>
                </div>
                <div className="border-t border-white/10 my-2"></div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Amount Financed</span>
                  <span>{formatCurrency(calculations.principal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Interest ({interestRate}% APR × {loanTerm} mo)</span>
                  <span>+ {formatCurrency(calculations.totalInterest)}</span>
                </div>
                <div className="border-t border-white/10 my-2"></div>
                <div className="flex justify-between font-semibold text-base">
                  <span>Total Cost</span>
                  <span>{formatCurrency(calculations.totalPayment)}</span>
                </div>
              </div>
            )}

            {/* CTA Button */}
            <Button
              onClick={() => navigate('/apply-now')}
              className="w-full bg-red-600 hover:bg-red-700 text-white py-3.5 rounded-full font-semibold text-lg transition-all hover:shadow-lg"
              data-testid="apply-now-btn"
            >
              Apply Now
            </Button>

            <p className="text-gray-500 text-xs text-center mt-4">
              * Estimates only. Actual rates, taxes, and terms may vary. Luxury tax applies to vehicles over $100,000 CAD.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LoanCalculator;
